import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { withServiceInstanceId } from '@/lib/service-instance'
import { isClubServiceVariant, isSocialServiceVariant } from '@/lib/service-variant-types'
import { cn } from '@/lib/utils'

type SectionLink = {
  title: string
  url: string
}

const FUNDRAISING_LINKS: SectionLink[] = [
  { title: 'Dashboard', url: '/fundraising/dashboard' },
  { title: 'Campaigns', url: '/fundraising' },
  { title: 'Donations', url: '/fundraising/donations' },
  { title: 'Donors', url: '/fundraising/donors' },
  { title: 'Categories', url: '/fundraising/categories' },
  { title: 'Settings', url: '/fundraising/settings' },
]

const EVENT_RESERVED = new Set([
  'dashboard',
  'customers',
  'assignments',
  'workdays',
  'configuration',
  'workflows',
])

const FUNDRAISING_RESERVED = new Set([
  'dashboard',
  'donations',
  'donors',
  'categories',
  'settings',
])

function isSectionActive(pathname: string, url: string) {
  if (url === '/fundraising') {
    if (pathname === '/fundraising') return true
    if (!pathname.startsWith('/fundraising/')) return false
    return !FUNDRAISING_RESERVED.has(pathname.split('/')[2] || '')
  }

  if (url === '/events') {
    if (pathname === '/events') return true
    if (!pathname.startsWith('/events/')) return false
    return !EVENT_RESERVED.has(pathname.split('/')[2] || '')
  }

  if (url === '/community/events' || url === '/club/events') {
    return pathname === url || pathname.startsWith(`${url}/`)
  }

  return pathname === url || pathname.startsWith(`${url}/`)
}

export function SectionTopNav() {
  const location = useLocation()
  const currentPost = useAuthStore((state) => state.currentPost)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const pathname = location.pathname
  const isFundraising = pathname === '/fundraising' || pathname.startsWith('/fundraising/')
  const isCommunityEvents = pathname === '/community/events' || pathname.startsWith('/community/events/')
  const isClubEvents = pathname === '/club/events' || pathname.startsWith('/club/events/')
  const isEvents = pathname === '/events' || pathname.startsWith('/events/') || isCommunityEvents || isClubEvents

  if (!isFundraising && !isEvents) {
    return null
  }

  if (pathname.startsWith('/fundraising/campaign/')) {
    return null
  }

  const variantId = currentPost?.service_variant_unique_id
  const variantLabel = currentPost?.service_variant_name ?? currentPost?.service_variant_type
  const isClub = isClubEvents || isClubServiceVariant(variantId, variantLabel)
  const isCommunity = !isClub && (
    isCommunityEvents || isSocialServiceVariant(variantId, variantLabel)
  )
  const eventsListUrl = isClub
    ? '/club/events'
    : isCommunity
      ? '/community/events'
      : '/events'

  const links: SectionLink[] = isFundraising
    ? FUNDRAISING_LINKS
    : [
      { title: 'Dashboard', url: isCommunity ? '/community/dashboard' : '/events/dashboard' },
      { title: 'Events', url: eventsListUrl },
    ]

  const params = new URLSearchParams(location.search)
  const serviceInstanceId = params.get('service_instance_id') || storedServiceInstanceId || ''

  return (
    <nav aria-label={isFundraising ? 'Fundraising' : 'Events'} className="mb-4 flex gap-2 overflow-x-auto pb-1">
      {links.map((link) => {
        const active = isSectionActive(pathname, link.url)
        return (
          <Link
            key={link.url}
            to={withServiceInstanceId(link.url, serviceInstanceId || undefined)}
            className={cn(
              'shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground hover:bg-accent',
            )}
            aria-current={active ? 'page' : undefined}
          >
            {link.title}
          </Link>
        )
      })}
    </nav>
  )
}
